'use client';

import {
  useAuthContext,
  USER_ID,
  federatedSignIn,
  AUTH_CONTEXT_KEYS,
} from '@r-and-a-shared-ui/auth-client';
import { getAllUserRolesFromChampionship } from '@r-and-a-shared-ui/common-ui';
import { useChampionshipPicker } from '@r-and-a-shared-ui/common-ui/src/hooks';
import {
  CURRENT_CHAMPIONSHIP_COLLECTION,
  redirectTo,
} from '@r-and-a-shared-ui/utils';
import { useEffect, useMemo, useRef } from 'react';
import { resolveChampionshipRedirect } from '../utils/access-fallback-redirect';

type AccessGateProps = {
  isProtected: boolean;
  children: React.ReactNode;
  pageRoles: string[];
  templateRoles: string[];
};

export const AccessGate = ({
  isProtected,
  pageRoles,
  templateRoles,
  children,
}: AccessGateProps) => {
  const { userState } = useAuthContext(AUTH_CONTEXT_KEYS.PP_CONTEXT_KEY);

  const {
    currentChampionship,
    availableChampionships,
    methods: { redirectToChampionship },
  } = useChampionshipPicker();

  const loginFallback = process.env.NEXT_PUBLIC_SIGN_IN_PAGE_FALLBACK ?? '/';

  const noAccessFallback =
    process.env.NEXT_PUBLIC_NO_ACCESS_PAGE_FALLBACK ?? '/';

  const oauthStartedRef = useRef(false);

  const isLoggedIn = userState?.isAuthenticated;
  const authReady = userState?.isAuthenticated !== null;

  const effectiveRoles = useMemo(
    () =>
      (pageRoles?.length > 0 ? pageRoles : templateRoles).map(role =>
        role.toLowerCase(),
      ),
    [pageRoles, templateRoles],
  );

  const userRoles = useMemo(
    () =>
      (
        getAllUserRolesFromChampionship(userState, currentChampionship) || []
      ).map(role => role.toLowerCase()),
    [userState, currentChampionship],
  );

  const rolesReady =
    !isLoggedIn || !isProtected || !effectiveRoles.length
      ? true
      : !!currentChampionship;

  const shouldRedirectToNoAccess =
    authReady &&
    rolesReady &&
    isLoggedIn &&
    effectiveRoles.length > 0 &&
    !userRoles.some(role => effectiveRoles.includes(role));

  const targetChampionship = resolveChampionshipRedirect(
    userState,
    currentChampionship,
    availableChampionships,
  );

  const shouldRedirectToAvailableChampionship =
    targetChampionship &&
    targetChampionship !== CURRENT_CHAMPIONSHIP_COLLECTION;

  useEffect(() => {
    if (!isProtected) return;

    const onPageShow = (e: PageTransitionEvent) => {
      if (!e.persisted) return;

      const userId = localStorage.getItem(USER_ID);

      if (userId) return;

      window.location.reload();
    };

    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, [isProtected]);

  const effectiveRedirect = useMemo(() => {
    if (!isProtected) {
      return null;
    }

    if (!authReady) {
      return null;
    }

    if (!isLoggedIn) {
      return loginFallback;
    }

    if (!rolesReady) {
      return null;
    }

    if (shouldRedirectToAvailableChampionship) {
      redirectToChampionship(targetChampionship);
      return;
    }

    if (shouldRedirectToNoAccess) {
      return noAccessFallback;
    }

    return null;
  }, [
    isProtected,
    authReady,
    isLoggedIn,
    rolesReady,
    loginFallback,
    noAccessFallback,
    shouldRedirectToNoAccess,
    targetChampionship,
  ]);

  useEffect(() => {
    if (!effectiveRedirect) {
      return;
    }

    if (effectiveRedirect === loginFallback) {
      if (oauthStartedRef.current) {
        return;
      }

      oauthStartedRef.current = true;
      federatedSignIn();
      return;
    }

    redirectTo(effectiveRedirect);
  }, [effectiveRedirect, loginFallback]);

  if (isProtected && (!authReady || !rolesReady)) {
    return null;
  }

  if (isProtected && shouldRedirectToAvailableChampionship) {
    return null;
  }

  if (effectiveRedirect) {
    return null;
  }

  return <>{children}</>;
};
